import { memo, useCallback } from 'react';
import { Stack } from '@mantine/core';
import { DeviceList } from '../../../admin/components/DeviceList';
import { DeviceTokenTable } from '../../../admin/components/DeviceTokenTable';
import {
  useUserDevices,
  useDeleteDeviceMutation,
  useInvalidateSingleDeviceTokenMutation,
} from '../../../hooks/useUserDevices';
import type { DeviceDYNCode, DeviceWithTokens } from '@types';

interface DeviceManagementProps {
  userId?: string;
  hideConnectionBadges?: boolean;
  hideCreateButtons?: boolean;
  showDeviceList?: boolean;
  showTokenTable?: boolean;
  onDeviceCreated?: () => void;
  onTokenInvalidated?: () => void;
}

export const DeviceManagement = memo<DeviceManagementProps>(
  ({
    userId,
    hideConnectionBadges = false,
    hideCreateButtons = false,
    showDeviceList = true,
    showTokenTable = true,
    onDeviceCreated,
    onTokenInvalidated,
  }) => {
    const {
      devices,
      devicesWithTokens,
      deviceTokens,
      isLoading,
      error,
      refetch,
    } = useUserDevices(userId);

    const deleteDeviceMutation = useDeleteDeviceMutation(userId);
    const invalidateSingleTokenMutation =
      useInvalidateSingleDeviceTokenMutation(userId);

    const handleRefresh = useCallback(() => {
      void refetch();
    }, [refetch]);

    const resolveDeviceId = useCallback(
      (token: DeviceDYNCode) => {
        if (token.deviceId) return token.deviceId;
        const device = devicesWithTokens.find(
          (item: DeviceWithTokens) =>
            item.code === token.deviceCode || item.clientId === token.deviceCode
        );
        return device ? device.id.toString() : '';
      },
      [devicesWithTokens]
    );

    const handleDeleteDevice = useCallback(
      async (deviceId: string) => {
        await deleteDeviceMutation.mutateAsync(deviceId);
        handleRefresh();
        onDeviceCreated?.();
      },
      [deleteDeviceMutation, handleRefresh, onDeviceCreated]
    );

    const resolveTokenIdentifier = useCallback((token: DeviceDYNCode) => {
      return token.deviceCode;
    }, []);

    const handleInvalidateToken = useCallback(
      async (token: DeviceDYNCode) => {
        const deviceId = resolveDeviceId(token);
        if (!deviceId) {
          throw new Error(
            'Impossibile determinare il dispositivo per il token'
          );
        }
        const deviceCode = resolveTokenIdentifier(token);
        if (!deviceCode) {
          throw new Error('Token session non valido');
        }
        await invalidateSingleTokenMutation.mutateAsync({
          deviceId,
          token: deviceCode,
        });
        handleRefresh();
        onTokenInvalidated?.();
      },
      [
        invalidateSingleTokenMutation,
        handleRefresh,
        onTokenInvalidated,
        resolveDeviceId,
        resolveTokenIdentifier,
      ]
    );

    if (!userId) {
      return <div>User ID is required to manage devices</div>;
    }

    const errorMessage = error
      ? error instanceof Error
        ? error.message
        : String(error)
      : null;

    return (
      <Stack gap='md'>
        {showDeviceList && (
          <DeviceList
            userId={userId}
            devices={devices}
            deviceTokens={[]}
            isLoading={isLoading}
            error={errorMessage}
            hideConnectionBadges={hideConnectionBadges}
            hideCreateButtons={hideCreateButtons}
            onDeviceCreated={() => {
              onDeviceCreated?.();
              handleRefresh();
            }}
            onRefresh={handleRefresh}
            onDeleteDevice={handleDeleteDevice}
          />
        )}
        {showTokenTable && (
          <DeviceTokenTable
            devices={devices}
            deviceTokens={deviceTokens}
            isLoading={isLoading}
            error={errorMessage}
            onRefresh={handleRefresh}
            onTokenInvalidated={onTokenInvalidated}
            onInvalidateToken={handleInvalidateToken}
          />
        )}
      </Stack>
    );
  }
);

DeviceManagement.displayName = 'DeviceManagement';
